import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { _ as _to_consumable_array } from "@swc/helpers/_/_to_consumable_array";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ActionIcon, Box, Combobox, Divider, factory, Text, TextInput, Title, useCombobox, useProps, useUncontrolled } from '@coveord/plasma-mantine';
import { Formatting, NumberFormatter } from '@core/format';
import { CrossSize16Px, FilterSize16Px } from '@coveord/plasma-react-icons';
import { clsx } from 'clsx';
import { DefaultFacetItem } from './DefaultFacetItem.js';
import classes from './Facet.module.css';
import { FacetScrollArea } from './FacetScrollArea.js';
import { groupOptions } from '../../utilities/group-options.js';
var defaultProps = {
    searchPlaceholder: 'Search',
    nothingFound: 'No matching items',
    placeholder: 'No items',
    height: 200,
    limit: Infinity,
    itemComponent: DefaultFacetItem,
    listComponent: FacetScrollArea,
    itemCountFormatter: Formatting.toFunction(NumberFormatter.integerFull)
};
export var Facet = factory(function(_props, ref) {
    var props = useProps('Facet', defaultProps, _props);
    var className = props.className, data = props.data, onChange = props.onChange, _props_initialSelection = props.initialSelection, initialSelection = _props_initialSelection === void 0 ? [] : _props_initialSelection, selection = props.selection, ItemComponent = props.itemComponent, ListComponent = props.listComponent, itemCountFormatter = props.itemCountFormatter, searchPlaceholder = props.searchPlaceholder, query = props.query, _props_hideSearch = props.hideSearch, hideSearch = _props_hideSearch === void 0 ? data.length <= 7 : _props_hideSearch, onSearch = props.onSearch, _props_filter = props.filter, filter = _props_filter === void 0 ? defaultFilter : _props_filter, nothingFound = props.nothingFound, placeholder = props.placeholder, title = props.title, height = props.height, radius = props.radius, __staticSelector = props.__staticSelector, classNames = props.classNames, styles = props.styles, limit = props.limit, unstyled = props.unstyled, others = _object_without_properties(props, [
        "className",
        "data",
        "onChange",
        "initialSelection",
        "selection",
        "itemComponent",
        "listComponent",
        "itemCountFormatter",
        "searchPlaceholder",
        "query",
        "hideSearch",
        "onSearch",
        "filter",
        "nothingFound",
        "placeholder",
        "title",
        "height",
        "radius",
        "__staticSelector",
        "classNames",
        "styles",
        "limit",
        "unstyled"
    ]);
    var combobox = useCombobox();
    var _useUncontrolled = _sliced_to_array(useUncontrolled({
        value: query,
        defaultValue: '',
        finalValue: '',
        onChange: onSearch
    }), 2), search = _useUncontrolled[0], handleSearch = _useUncontrolled[1];
    var _useUncontrolled1 = _sliced_to_array(useUncontrolled({
        value: selection,
        defaultValue: initialSelection,
        finalValue: [],
        onChange: onChange
    }), 2), _selection = _useUncontrolled1[0], handleSelection = _useUncontrolled1[1];
    var unGroupedItems = [];
    var groupedItems = [];
    var filteredData = data.filter(function(item) {
        return filter(search, item);
    }).slice(0, limit);
    var sortedData = groupOptions({
        data: filteredData
    });
    var handleValueSelect = function handleValueSelect(val) {
        return handleSelection(_selection.includes(val) ? _selection.filter(function(v) {
            return v !== val;
        }) : _to_consumable_array(_selection).concat([
            val
        ]));
    };
    var groupName = null;
    sortedData.forEach(function(item) {
        var itemComponent = /*#__PURE__*/ _jsx(Combobox.Option, {
            active: _selection.includes(item.value),
            "aria-selected": _selection.includes(item.value),
            value: item.value,
            onMouseEnter: function onMouseEnter() {
                return combobox.resetSelectedOption();
            },
            className: clsx(classes.facetItem),
            tabIndex: data.length > 8 ? -1 : 1,
            children: /*#__PURE__*/ _jsx(ItemComponent, {
                data: item,
                selected: _selection.includes(item.value),
                countFormatter: itemCountFormatter
            })
        }, item.value);
        if (!item.group) {
            unGroupedItems.push(itemComponent);
        } else {
            if (groupName !== item.group) {
                groupName = item.group;
                groupedItems.push(/*#__PURE__*/ _jsx("div", {
                    className: classes.separator,
                    children: /*#__PURE__*/ _jsx(Divider, {
                        classNames: {
                            label: classes.separatorLabel
                        },
                        label: groupName
                    })
                }, groupName));
            }
            groupedItems.push(itemComponent);
        }
    });
    if (groupedItems.length > 0 && unGroupedItems.length > 0) {
        unGroupedItems.unshift(/*#__PURE__*/ _jsx("div", {
            className: classes.separator,
            children: /*#__PURE__*/ _jsx(Divider, {
                unstyled: unstyled,
                classNames: {
                    label: classes.separatorLabel
                }
            })
        }));
    }
    return /*#__PURE__*/ _jsx(Box, _object_spread_props(_object_spread({
        className: clsx(classes.facet, className)
    }, others), {
        children: /*#__PURE__*/ _jsxs(Combobox, {
            store: combobox,
            onOptionSubmit: handleValueSelect,
            children: [
                /*#__PURE__*/ _jsx(Combobox.EventsTarget, {
                    children: /*#__PURE__*/ _jsxs(Box, {
                        className: classes.facetHeader,
                        children: [
                            title ? /*#__PURE__*/ _jsx(Title, {
                                order: 5,
                                children: title
                            }) : null,
                            /*#__PURE__*/ _jsx(TextInput, {
                                unstyled: unstyled,
                                value: search,
                                onChange: function onChange(event) {
                                    handleSearch(event.currentTarget.value);
                                    combobox.updateSelectedOptionIndex();
                                },
                                placeholder: searchPlaceholder,
                                "aria-hidden": hideSearch,
                                className: clsx(classes.facetSearch, _define_property({}, classes.hiddenSearch, hideSearch)),
                                rightSection: search ? /*#__PURE__*/ _jsx(ActionIcon.Quaternary, {
                                    "aria-label": "clear search",
                                    color: "gray",
                                    onClick: function onClick() {
                                        handleSearch('');
                                    },
                                    children: /*#__PURE__*/ _jsx(CrossSize16Px, {
                                        height: 16
                                    })
                                }) : /*#__PURE__*/ _jsx(FilterSize16Px, {
                                    height: 16
                                })
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: classes.facetBody,
                    children: /*#__PURE__*/ _jsx(ListComponent, {
                        className: classes.facetItems,
                        mah: height,
                        style: {
                            overflow: 'auto',
                            position: 'relative'
                        },
                        children: /*#__PURE__*/ _jsx(Combobox.Options, {
                            children: groupedItems.length > 0 || unGroupedItems.length > 0 ? /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    groupedItems,
                                    unGroupedItems
                                ]
                            }) : /*#__PURE__*/ _jsx(Combobox.Empty, {
                                children: /*#__PURE__*/ _jsx(Text, {
                                    color: "dimmed",
                                    unstyled: unstyled,
                                    size: "sm",
                                    ta: "center",
                                    my: "sm",
                                    children: !query && placeholder ? placeholder : nothingFound
                                })
                            })
                        })
                    })
                })
            ]
        })
    }));
});
Facet.displayName = 'Facet';
var defaultFilter = function defaultFilter(query, item) {
    return item.label.toLowerCase().trim().includes(query.toLowerCase().trim()) || item.value.toLowerCase().trim().includes(query.toLowerCase().trim());
};

//# sourceMappingURL=Facet.js.map